/* eslint-disable camelcase */
import classNames from "classnames";
import { chain, extend, map, omit } from "lodash";
import AppContext from "PFApp/app_context";
import DownloadLink from "PFApp/components/chat/download_link";
import {
  getCustomFieldNames,
  getVisibleProperties,
  propertyToTitle,
  useTemplate
} from "PFCore/helpers/templates";
import { usePropertyToString } from "PFCore/helpers/use_property_to_string";
import LocationIcon from "PFIcons/pin.svg";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { dasherize } from "underscore.string";

import css from "./activity_details_common.scss";

export const useMetadata = ({ activity, metadataToOmit = [], valuesSeparator }) => {
  const {
    store: { customTypes }
  } = useContext(AppContext);
  const propertyToString = usePropertyToString();
  const template = useTemplate(activity?.template_id);

  if (!activity || !template) {
    return [];
  }

  const metadataCfValues = (() => {
    const cfNames = getCustomFieldNames(template);
    const metadataCfs = (customTypes || []).filter((cf) => cfNames.includes(cf.name));

    const cfTypeIds = metadataCfs.map(({ id }) => id);

    // opposite of getNonMetadataCfValues
    return (
      chain(activity.custom_fields)
        // Added ? to cfVal as there is a condition on the update in which type is not present
        .filter((cfVal) => cfTypeIds.includes(cfVal?.type?.id))
        .map((cfVal) => ({
          name: metadataCfs.find(({ id }) => id === cfVal?.type?.id)?.name,
          values: cfVal.values
        }))
        .reduce((memo, val) => {
          // turn array into object
          memo[val.name] = (memo[val.name] || []).concat(val.values);
          return memo;
        }, {})
        .value()
    );
  })();

  const metadata = omit(extend({}, activity.metadata, metadataCfValues), metadataToOmit);

  return getVisibleProperties(template)
    .filter((prop) => metadata[prop.name] != null) // eslint-disable-line eqeqeq
    .map((prop) => {
      const ct = (customTypes || []).find(({ name }) => name === prop.name);
      const valueType = ct ? ct?.value_type : null;
      const text = propertyToString(template, prop.name, metadata[prop.name], valueType, valuesSeparator);

      return {
        title: propertyToTitle(template, prop.name, customTypes),
        text: text,
        raw: metadata[prop.name],
        type: prop.type
      };
    });
};

const ActivityDetailsMoreInfo = ({ activity, attachments, metadataToOmit, className }) => {
  const { t } = useTranslation("activities");
  const metadata = useMetadata({ activity, metadataToOmit });
  const isSeparatorVisible = metadata?.filter((item) => item.text)?.length > 0;

  // wrapping div is important here because we target last-child with css
  return (
    <div
      data-qa-id="ActivityDetailsMoreInfo"
      className={classNames(css.infoMore, { [className]: isSeparatorVisible })}
    >
      {map(
        metadata,
        (prop) =>
          prop.text && (
            <div
              className={css.property}
              key={prop.title}
              data-qa-id={`activity-details-metadata-prop${dasherize(prop.title)}`}
            >
              <div className={css.infoItem}>
                <strong className={css.textOverflow}>{prop.title}</strong>
                <div className={css.description}>
                  {prop.type === "link" ? (
                    <a href={prop.text} target="_blank" rel="noopener noreferrer">
                      {prop.text}
                    </a>
                  ) : (
                    <div className={prop.type === "availability" ? css.vertical : css.inlineIcon}>
                      {prop.title === "Location" && <LocationIcon height={20} width={20} />}
                      <span style={{ whiteSpace: "pre-line" }}> {prop.text} </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
      )}

      {attachments && attachments.length > 0 && (
        <>
          <div className={css.topLine} />
          <div className={css.infoItem}>
            <strong>{t("show.details.attachments")}</strong>
            <ul className={css.attachments}>
              {attachments.map((attachment) => (
                <li key={attachment.id}>
                  <DownloadLink attachment={attachment} useIcon={false}>
                    {attachment.file_name}
                  </DownloadLink>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

ActivityDetailsMoreInfo.propTypes = {
  activity: PropTypes.object,
  attachments: PropTypes.array,
  metadataToOmit: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string
};

export default ActivityDetailsMoreInfo;
